/**
 * The events row's standard look (the prototype's Upcoming events row, card A).
 * Draws the behavior half's state and calls its actions; holds no rule. The
 * track scrolls smoothly to the view the behavior half holds and reports a
 * swipe back to it. Draws nothing until there is at least one event.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { partsOf, pieceRoot, usePlaced } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { onLink } from '../../ui/onLink';
import { EventCard } from './EventCard';
import type { EventsRowRuntime } from './useEventsRow';
import css from './EventsRow.module.css';
import { contract } from './contract';

const part = partsOf(contract);

export function EventsRowView({ state, actions, options }: EventsRowRuntime) {
  const trackRef = useRef<HTMLUListElement>(null);
  // The view the track is at, so a scroll the piece asked for is not reported back as a swipe.
  const shownPage = useRef(state.page);
  const [arrowTop, setArrowTop] = useState<number | null>(null);
  // Placed on a custom page (web-studio-sites D4): no band, no frame, no fixed anchor.
  const placed = usePlaced();
  const placedTitle = useId();
  const titleId = placed ? placedTitle : 'eventsTitle';

  useEffect(() => {
    if (shownPage.current === state.page) return;
    shownPage.current = state.page;
    const track = trackRef.current;
    const first = track?.children[0] as HTMLElement | undefined;
    const target = track?.children[state.firstInView] as HTMLElement | undefined;
    if (!track || !first || !target) return;
    track.scrollTo?.({ left: target.offsetLeft - first.offsetLeft, behavior: 'smooth' });
  }, [state.page, state.firstInView]);

  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const page =
      track.scrollLeft >= max - 2 ? state.pageCount - 1 : Math.round(track.scrollLeft / (track.clientWidth + gap));
    if (page === shownPage.current) return;
    shownPage.current = page;
    actions.scrolledTo(page);
  }, [actions, state.pageCount]);

  // The arrows sit centered on the photos, as the prototype measures them.
  const hasEvents = state.events.length > 0;
  useLayoutEffect(() => {
    if (!hasEvents || !state.hasMore) return;
    const measure = () => {
      const photo = trackRef.current?.querySelector<HTMLElement>('[data-part="photo"]');
      setArrowTop(photo ? photo.offsetTop + photo.offsetHeight / 2 : null);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [hasEvents, state.hasMore, options.showPhoto]);

  if (!hasEvents) return null;
  const arrowStyle = arrowTop != null ? { top: `${arrowTop}px` } : undefined;
  const anyPhoto = state.events.some((e) => e.photo);

  return (
    <section
      className={placed ? undefined : shared.sec}
      id={placed ? undefined : 'events'}
      aria-labelledby={titleId}
      {...pieceRoot(contract)}
      {...part('section')}
    >
      <div className={placed ? undefined : shared.wrap} {...part('wrap')}>
        {(options.heading || options.eyebrow || state.hasMore) && (
          <div className={shared.secHead} {...part('head')}>
            <div {...part('titles')}>
              {options.eyebrow && (
                <div className={shared.eyebrow} {...part('eyebrow')}>
                  {options.eyebrow}
                </div>
              )}
              {options.heading && (
                <h2 className={shared.h2} id={titleId} {...part('heading')}>
                  {options.heading}
                </h2>
              )}
            </div>
            {state.hasMore && (
              <a
                className={`${shared.go} ${css.evAll}`}
                href={state.allEventsPath}
                onClick={onLink(actions.seeAll)}
                {...part('see-all')}
              >
                <u>See all events</u>
                <Icon name="arrow" small />
              </a>
            )}
          </div>
        )}
        <div className={css.evRow} {...part('row')}>
          <ul
            ref={trackRef}
            className={css.evTrack}
            tabIndex={-1}
            aria-label={options.heading ?? 'Upcoming events'}
            onScroll={onScroll}
            {...part('track')}
          >
            {state.events.map((e) => (
              <EventCard
                key={e.id}
                className={css.slide}
                event={e}
                timezone={state.timezone}
                showPhoto={options.showPhoto && anyPhoto}
                showSummary={options.showSummary}
                mark={state.marks[e.id]}
                onOpen={() => actions.open(e.id)}
                onOpenBooking={() => actions.openBooking(e.id)}
              />
            ))}
          </ul>
          {state.canPrevious && (
            <button
              type="button"
              className={`${css.evArrow} ${css.evPrev}`}
              style={arrowStyle}
              aria-label="Earlier events"
              onClick={actions.previous}
              {...part('previous')}
            >
              <Icon name="left" />
            </button>
          )}
          {state.canNext && (
            <button
              type="button"
              className={`${css.evArrow} ${css.evNext}`}
              style={arrowStyle}
              aria-label="Later events"
              onClick={actions.next}
              {...part('next')}
            >
              <Icon name="right" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
